import React, { createContext, useContext, useEffect, useState } from 'react';
import { getUserToken } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/** Provided ONLY by the admin dashboard around customer views. Its presence switches AuthImage to the reason-gated admin path. */
export const AdminPhotoAccessContext = createContext(null);

/**
 * D-13: renders a meal photo fetched from the app's own photo endpoint as a blob URL.
 * Token selection is EXPLICIT by role - never a fallback chain over browser storage:
 *  - user view (default): the signed-in USER's token only (memory > sessionStorage.auth_token > localStorage.token).
 *    An admin_session_token that happens to sit in the same browser tab is never used here.
 *  - admin view: `adminToken` prop or AdminPhotoAccessContext; every photo needs a typed reason first (prompt), sent as ?reason= and
 *    written to photo_access_log by the server.
 */
const AuthImage = ({ src, alt, className, testId, reason: reasonProp, adminToken: adminTokenProp, thumb = false, ...rest }) => {
  const adminCtx = useContext(AdminPhotoAccessContext);
  const adminToken = adminTokenProp || adminCtx?.token || null;
  const adminMode = Boolean(adminToken);
  const [reasonState, setReasonState] = useState(null);
  const reason = reasonProp || reasonState;
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const tid = testId || 'photo';

  useEffect(() => {
    if (!src || !src.startsWith('/api/')) return undefined; // only the app's own photo path is ever fetched
    if (adminMode && !reason) return undefined;             // admin view is reason-gated; nothing is requested without one
    const token = adminMode ? adminToken : getUserToken();
    if (!token) { setFailed(true); return undefined; }
    let objectUrl; const controller = new AbortController();
    setFailed(false); setBlobUrl(null);
    if (!adminMode) {
      // Q8-3: owner view asks for a short-lived link (10 min, bound to this entry) and lets the browser load + privately cache the image;
      // lists ask for the small thumbnail. No bearer header travels with the image request and no blob is held in memory.
      fetch(`${API}${src}-link${thumb ? '?size=thumb' : ''}`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal })
        .then((r) => { if (!r.ok) throw new Error(`photo-link ${r.status}`); return r.json(); })
        .then((j) => setBlobUrl(`${API}${j.url}`))
        .catch((e) => { if (e.name !== 'AbortError') setFailed(true); });
      return () => controller.abort();
    }
    const url = `${API}${src}?reason=${encodeURIComponent(reason)}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store', signal: controller.signal })
      .then((r) => { if (!r.ok) throw new Error(`photo ${r.status}`); return r.blob(); })
      .then((b) => { objectUrl = URL.createObjectURL(b); setBlobUrl(objectUrl); })
      .catch((e) => { if (e.name !== 'AbortError') setFailed(true); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [src, reason, adminToken, adminMode, thumb]);

  const askReason = (e) => {
    e.stopPropagation(); e.preventDefault(); // the card behind the button opens a detail modal on click
    const r = window.prompt('Reason for viewing this customer photo (recorded in the audit log, min 8 chars):');
    if (r && r.trim().length >= 8) setReasonState(r.trim());
  };

  if (!src || !src.startsWith('/api/') || failed) return <div className={`${className} bg-muted flex items-center justify-center text-xs text-muted-foreground`} data-testid={`${tid}-unavailable`}>{failed ? 'Photo unavailable' : ''}</div>;
  if (adminMode && !reason) return <button type="button" onClick={askReason} className={`${className} border border-dashed bg-muted/40 text-[10px] text-muted-foreground hover:bg-muted flex items-center justify-center text-center`} data-testid={`${tid}-admin-view-btn`}>View photo (audited)</button>;
  if (!blobUrl) return <div className={`${className} bg-muted animate-pulse`} data-testid={`${tid}-loading`} />;
  return <img src={blobUrl} alt={alt} className={className} loading="lazy" onError={() => setFailed(true)} data-testid={testId} data-token-role={adminMode ? 'admin' : 'user'} data-thumb={thumb ? 'true' : undefined} {...rest} />;
};

export default AuthImage;
